import { useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { SessionStatus, selectStatus, useSession } from "@core/auth";
import { describeError } from "@core/api";
import { useToaster } from "pixelize-design-library";
import { uploadSearchImage } from "@modules/shared";

export const useHeaderVisualSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const status = useSession(selectStatus);
  const { showToast } = useToaster();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const onFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.currentTarget.files?.[0];
      event.currentTarget.value = "";
      if (!file) return;
      setIsUploading(true);
      try {
        const imageUrl = await uploadSearchImage(file);
        void navigate(`/search/visual?imageUrl=${encodeURIComponent(imageUrl)}`);
      } catch (error) {
        showToast({
          status: "error",
          title: "Could not search by photo",
          description: describeError(error),
        });
      } finally {
        setIsUploading(false);
      }
    },
    [navigate, showToast],
  );

  const openPicker = useCallback(
    (mode: "camera" | "gallery") => {
      if (isUploading) return;
      if (status !== SessionStatus.AUTHENTICATED) {
        void navigate("/login", { state: { from: location.pathname } });
        return;
      }
      (mode === "camera" ? cameraInputRef : galleryInputRef).current?.click();
    },
    [isUploading, status, navigate, location.pathname],
  );

  return {
    cameraInputRef,
    galleryInputRef,
    isUploading,
    onFileChange,
    openPicker,
  };
};
